import type { AbsenceValues, CoverageValues, Employee, SettingType } from '@/types/app';

/**
 * Ordered steps of the quote-settings confirmation wizard. The client is guided
 * through every still-unconfirmed step (one warning triangle at a time) before a
 * single recalculation is submitted.
 */
export const WIZARD_STEPS = ['employees', 'absence', 'coverage'] as const;

export interface WizardSources {
	employees: string | null;
	absence: string | null;
	coverage: string | null;
}

export interface WizardFlags {
	allowUserCoverageOverride: boolean;
}

/**
 * Sources that count as already confirmed/trusted: the data is shown with a green
 * checkmark and needs no confirmation panel. This is the single source of truth for
 * "is this trusted?" — both the green-check icon (`SourceIcon`) and the wizard's
 * pending-step decision derive from it, so the checkmark and the panel-open behaviour
 * can never drift apart.
 */
export const TRUSTED_SOURCES = [
	'client',
	'current_policy',
	'initial_payload',
	'employee_list_extraction'
] as const;

export function isSourceTrusted(source: string | null): boolean {
	return source != null && (TRUSTED_SOURCES as readonly string[]).includes(source);
}

/**
 * A step is "pending" when its summary card shows a warning triangle and the user can
 * still resolve it — i.e. its source is not yet trusted (see `isSourceTrusted`). The
 * coverage step additionally requires coverage editing to be enabled for the origin.
 */
export function isStepPending(
	step: SettingType,
	sources: WizardSources,
	flags: WizardFlags
): boolean {
	switch (step) {
		case 'employees':
			return !isSourceTrusted(sources.employees);
		case 'absence':
			return !isSourceTrusted(sources.absence);
		case 'coverage':
			return flags.allowUserCoverageOverride && !isSourceTrusted(sources.coverage);
		default:
			return false;
	}
}

/**
 * Step the visitor (human or agent) should act on next: the first step that is still
 * pending (warning triangle) and has not been optimistically confirmed in the current
 * wizard session. Returns null once nothing remains. Steps the user just confirmed this
 * session are skipped even though their source only turns trusted after the deferred
 * commit, matching what the green checkmark already shows. This is the single "what's
 * still outstanding?" rule shared by the top guidance bar and the panel that Kiezen
 * opens, so the bar, the checkmarks and Kiezen can never disagree. Pass an empty set for
 * the plain "first pending step, ignoring session confirmations".
 */
export function getNextGuidanceStep(
	sources: WizardSources,
	flags: WizardFlags,
	confirmedSteps: ReadonlySet<SettingType>
): SettingType | null {
	return (
		WIZARD_STEPS.find((step) => !confirmedSteps.has(step) && isStepPending(step, sources, flags)) ??
		null
	);
}

/**
 * Next step after `currentStep` that still needs confirmation, or null when
 * `currentStep` is the last pending step (i.e. the terminal "submit" step).
 */
export function getNextPendingStep(
	currentStep: SettingType,
	sources: WizardSources,
	flags: WizardFlags
): SettingType | null {
	const startIdx = WIZARD_STEPS.indexOf(currentStep);
	if (startIdx === -1) return null;
	return (
		WIZARD_STEPS.slice(startIdx + 1).find((step) => isStepPending(step, sources, flags)) ?? null
	);
}

/**
 * Next pending step the user still has to confirm in the current wizard session,
 * or null when `currentStep` is the terminal step (every other pending step has
 * already been confirmed this session). Drives "confirm one ⇒ confirm all": the
 * user is routed through all remaining warnings before a single recalculation,
 * regardless of which step they entered from.
 */
export function getNextUnconfirmedStep(
	currentStep: SettingType,
	sources: WizardSources,
	flags: WizardFlags,
	confirmedSteps: ReadonlySet<SettingType>
): SettingType | null {
	return (
		WIZARD_STEPS.find(
			(step) =>
				step !== currentStep && !confirmedSteps.has(step) && isStepPending(step, sources, flags)
		) ?? null
	);
}

export interface WizardSaveValues {
	employeeList: Employee[];
	absenceValues: AbsenceValues | null;
	coverageValues: CoverageValues | null;
}

export interface WizardSavePayload {
	employees: Employee[] | null;
	absenceValues: AbsenceValues | null;
	coverageValues: CoverageValues | null;
}

/**
 * Build the `premiumVarManagement.save` payload for the deferred wizard commit,
 * including a step's value **iff the user confirmed that step this session**. This
 * is what lets a click-outside commit exactly the confirmed cards (Medewerkers /
 * Verzuim) while omitting a card that was opened/edited but never confirmed.
 */
export function buildConfirmedPayload(
	confirmedSteps: ReadonlySet<SettingType>,
	values: WizardSaveValues
): WizardSavePayload {
	return {
		employees: confirmedSteps.has('employees') ? values.employeeList : null,
		absenceValues: confirmedSteps.has('absence') ? values.absenceValues : null,
		coverageValues: confirmedSteps.has('coverage') ? values.coverageValues : null
	};
}
